import React, {useContext, useState} from 'react';
import {Input} from '../../dimijs/src/lib/components/Input';
import {Button} from '../../dimijs/src/lib/components/Button';
import {useUserSource} from '../../sources/useUserSource';
import axios from 'axios';
import {IBlockProps} from './Blocks';
import styles from './Login.module.scss';
import classnames from 'classnames';
import {DataCtx} from '../../dimijs/src/lib/utils/dataContext';

const auth = async (username, password) => {
  try {
    await axios.get('/authenticate', {auth: {username, password}});
    return true;
  } catch (e) {
    return false;
  }
};

export interface IAdditionalLoginBlockProps {
  loginTitle: string;
  signupTitle: string;
  defaultSlide?: string;
}
export interface ILoginBlockProps
  extends IBlockProps,
    IAdditionalLoginBlockProps {}

export const Login = (props: ILoginBlockProps) => {
  const [slide, setSlide] = useState(props.defaultSlide || 'login');
  const [login, setLogin] = useState('');
  const [pass, setPass] = useState('');
  const [email, setEmail] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const userSource = useUserSource();
  const {initProps} = useContext(DataCtx);

  const handleSignup = async () => {
    setError('');
    setSuccess('');

    if (!email || !pass) {
      setError('Email and password are required');
      return;
    }

    if (pass !== confirmPass) {
      setError('Passwords do not match');
      return;
    }

    if (pass.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      const response = await axios.post('/api/auth/signup', {
        email,
        password: pass,
      });

      if (response.data.success) {
        setSuccess('Account created! Please check your email to verify your account.');
        setEmail('');
        setPass('');
        setConfirmPass('');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Signup failed. Please try again.');
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setSuccess('');

    if (!email) {
      setError('Email is required');
      return;
    }

    try {
      const response = await axios.post('/api/auth/forgot-password', {email});

      if (response.data.success) {
        setSuccess('If an account with that email exists, a password reset link has been sent.');
        setEmail('');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to send reset email. Please try again.');
    }
  };

  if (initProps.user)
    return null;
  return (
    <div className={classnames(styles.slider, 'relative')}>
      <div
        className={classnames(
          styles.flexSlides,
          slide === 'signup' && styles.moveToSignup,
          slide === 'forgot' && styles.moveToForgot,
        )}
        style={{
          display: 'flex',
          transition: 'transform 0.3s ease',
          transform: slide === 'signup' ? 'translateX(-33.3333%)' : slide === 'forgot' ? 'translateX(-66.6666%)' : 'translateX(0)'
        }}>
        {/* Login Slide */}
        <div className={classnames(styles.slide)}>
          <h3>{props.loginTitle || 'Login'}</h3>
          {error && <div style={{color: 'red', marginBottom: '10px'}}>{error}</div>}
          {success && <div style={{color: 'green', marginBottom: '10px'}}>{success}</div>}
          <form onSubmit={(e) => e.preventDefault()}>
            <Input value={login} onChange={setLogin} label={'Email'} type='email' />
            <Input value={pass} onChange={setPass} label={'Password'} type='password'/>
            <Button
              caption="Login"
              type={'submit'}
              color={'primary'}
              onClick={() => {
                setError('');
                setSuccess('');
                auth(login, pass).then(result => {
                  if (result) window.location.reload();
                  else setError('Invalid login or password');
                });
              }}
            />
          </form>
          <div className={styles.divider}>
            <span>or</span>
          </div>
          <a href="/auth/google" className={styles.googleBtn}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </a>
          <p>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setSlide('forgot');
              }}>
              Forgot password?
            </a>
          </p>
          <p>
            Don't have an account?{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setSlide('signup');
              }}>
              Register
            </a>
          </p>
        </div>

        {/* Signup Slide */}
        <div className={classnames(styles.slide)}>
          <h3>{props.signupTitle || 'Sign Up'}</h3>
          {error && <div style={{color: 'red', marginBottom: '10px'}}>{error}</div>}
          {success && <div style={{color: 'green', marginBottom: '10px'}}>{success}</div>}
          <form onSubmit={(e) => e.preventDefault()}>
            <Input value={email} onChange={setEmail} label={'Email'} type='email' />
            <Input value={pass} onChange={setPass} label={'Password'} type='password' />
            <Input value={confirmPass} onChange={setConfirmPass} label={'Confirm Password'} type='password' />
            <Button
              caption="Sign Up"
              color={'primary'}
              onClick={handleSignup}
            />
          </form>
          <p>
            Already have an account?{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setSlide('login');
              }}>
              Login
            </a>
          </p>
        </div>

        {/* Forgot Password Slide */}
        <div className={classnames(styles.slide)}>
          <h3>Forgot Password</h3>
          {error && <div style={{color: 'red', marginBottom: '10px'}}>{error}</div>}
          {success && <div style={{color: 'green', marginBottom: '10px'}}>{success}</div>}
          <form onSubmit={(e) => e.preventDefault()}>
            <Input value={email} onChange={setEmail} label={'Email'} type='email' />
            <Button
              caption="Send Reset Link"
              color={'primary'}
              onClick={handleForgotPassword}
            />
          </form>
          <p>
            Remember your password?{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setSlide('login');
              }}>
              Login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
